import * as React from "react";
import Button from "../Button";
import { FormWidgetConfig, RelayRequest } from "../../types";
import { useState, useEffect, useCallback, useRef } from "react";
import RelayService from "../../services/RelayService";
import FieldError from "../FieldError";

interface Props {
  config: FormWidgetConfig;
  index: number;
}

export default function FormWidget(props: Props) {
  const [formData, setFormData] = useState("");
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(false);
  const [resultsHtml, setResultsHtml] = useState<string>(null);

  const mountedRef = useRef(true);
  const resultsRef = useRef<HTMLDivElement>();

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView();
    }
  }, [resultsHtml]);

  const submitForm = useCallback(async () => {
    if (fetching) {
      return;
    }

    setFetching(true);
    setError(false);

    const request: RelayRequest = {
      Data: {
        FormData: formData
      },
      BasicAuthHeader: props.config.EndpointAuth,
      Url: props.config.EndpointUrl
    };

    let responseStr: string = null;
    try {
      responseStr = await RelayService.relayRequest(request);
      if (!mountedRef.current) {
        return;
      }
    } catch (e) {
      if (!mountedRef.current) {
        return;
      }
      console.error(e);
      setError(true);
    }

    setFetching(false);

    if (responseStr) {
      const response = JSON.parse(responseStr);
      console.log("Relay response!", response);
      if (response.ResultHtml) {
        setResultsHtml(response.ResultHtml);
      }
    }
  }, [fetching, formData, props.config.EndpointAuth, props.config.EndpointUrl]);

  if (!props.config || !props.config.Enabled) {
    return null;
  }

  return (
    <>
      <div className={`landing-widget form-widget-${props.index}`}>
        {props.config.SideImageUrl && (
          <>
            {props.config.SideImageGradientEnabled && (
              <div className="widget-gradient" />
            )}
            <div className="widget-side-img" />
          </>
        )}
        <div className="widget-body">
          <div className="widget-message">{props.config.LabelText}</div>
          <div className="widget-divider" />
          <div className="widget-form-container">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitForm();
              }}
            >
              <div className="velaro-form-group">
                <label>{props.config.InputLabel}</label>
                <input
                  required
                  type="text"
                  value={formData}
                  onChange={(e) => setFormData(e.currentTarget.value)}
                  aria-label={props.config.InputLabel}
                />
                {error && (
                  <FieldError>
                    {"We're sorry. There was a problem performing the request."}
                  </FieldError>
                )}
              </div>
              <Button
                disabled={fetching}
                type="submit"
                className="landing-widget-button"
                variant="primary"
              >
                {fetching ? "Loading..." : props.config.ButtonLabel}
              </Button>
            </form>
          </div>
        </div>
      </div>
      {resultsHtml && (
        <>
          <div ref={resultsRef} className="landing-widget form-widget">
            <div className="widget-body">
              <iframe srcDoc={resultsHtml} />
            </div>
          </div>
        </>
      )}
    </>
  );
}
